import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { CheckCircle, ArrowRight, ArrowLeft, ArrowUp, FloppyDisk, X, Sparkle, FileText, Plus, Trash, BookOpen } from "@phosphor-icons/react";
import { DateInput } from "../components/ui/date-input";
import { fmtDateIN } from "../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/joiner-form`;

const SECTIONS = [
  { key: "personal",                title: "Personal details",        required: true  },
  { key: "contact",                 title: "Contact & address",       required: true  },
  { key: "emergency_contacts",      title: "Emergency contacts",      required: true  },
  { key: "statutory_ids",           title: "PAN, Aadhaar & IDs",      required: true  },
  { key: "education",               title: "Education",               required: true  },
  { key: "previous_employment",     title: "Previous employment",     required: true  },
  { key: "banking",                 title: "Banking",                 required: true  },
  { key: "family",                  title: "Family & nominees",       required: false },
  { key: "skills",                  title: "Skills & profile",        required: false },
  { key: "references",              title: "References",              required: false },
  { key: "medical",                 title: "Medical (optional)",      required: false },
  { key: "visa",                    title: "Visa / work permit",      required: false },
  { key: "background_consent",      title: "Background check consent",required: true  },
  { key: "policy_acknowledgments",  title: "Policy acknowledgments",  required: true  },
  { key: "joining_logistics",       title: "Joining logistics",       required: true  },
  { key: "review",                  title: "Review & submit",         required: false, isReview: true },
];

async function readBase64(file) {
  return new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(String(fr.result || "").split(",")[1] || "");
    fr.onerror = rej;
    fr.readAsDataURL(file);
  });
}

export default function JoinerWelcomeForm() {
  const { token } = useParams();
  const [state, setState] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [step, setStep] = useState(0);
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState({});

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/${token}`),
      axios.get(`${API}/catalog`),
    ]).then(([s, c]) => {
      setState(s.data); setCatalog(c.data);
      setData(s.data.form_draft || {});
    }).catch((e) => {
      const msg = e?.response?.data?.detail || "Could not load your form";
      toast.error(msg);
      setState({ error: msg });
    });
  }, [token]);

  const saveSection = async (sectionKey, sectionData) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/${token}`, { section: sectionKey, data: sectionData });
      setState((s) => ({ ...s, completion_pct: r.data.completion_pct }));
      setData((d) => ({ ...d, [sectionKey]: sectionData }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const uploadFile = async (field_path, file) => {
    if (file.size > 5 * 1024 * 1024) return toast.error("Max 5MB per file");
    setUploading((u) => ({ ...u, [field_path]: true }));
    try {
      const file_data_b64 = await readBase64(file);
      const r = await axios.post(`${API}/${token}/upload`, {
        field_path, file_name: file.name, file_data_b64,
        mime_type: file.type || "application/pdf",
      });
      setState((s) => ({
        ...s,
        uploads: { ...(s.uploads || {}), [field_path]: { file_name: file.name, size_bytes: r.data.size_bytes } },
      }));
      toast.success(`${file.name} uploaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading((u) => ({ ...u, [field_path]: false }));
    }
  };

  const removeUpload = async (field_path) => {
    try {
      await axios.delete(`${API}/${token}/upload/${field_path}`);
      setState((s) => {
        const u = { ...(s.uploads || {}) };
        delete u[field_path];
        return { ...s, uploads: u };
      });
    } catch (e) {
      toast.error("Could not remove");
    }
  };

  const submitReadiness = useMemo(() => {
    const policies = (catalog?.policies || []).map((p) => p.key);
    const reqSections = catalog?.required_sections || [];
    const missing = [];
    reqSections.forEach((s) => { if (!data[s]) missing.push(s); });
    const personal = data.personal || {};
    if (!personal.full_legal_name) missing.push("full_legal_name");
    const statutory = data.statutory_ids || {};
    if (!statutory.pan && !statutory.no_pan) missing.push("pan");
    const banking = data.banking || {};
    if (!banking.cash_payment && !banking.account_number) missing.push("account_number");
    if (!banking.cash_payment && !banking.ifsc) missing.push("ifsc");
    if (banking.account_number && banking.account_number_confirm &&
        banking.account_number !== banking.account_number_confirm) {
      missing.push("account_number_mismatch");
    }
    const acks = data.policy_acknowledgments || {};
    policies.forEach((k) => { if (!acks[k]) missing.push(`policy_${k}`); });
    const consent = data.background_consent || {};
    if (!consent.bgv_consent) missing.push("bgv_consent");
    if (!consent.posh_briefing_consent) missing.push("posh_briefing_consent");
    return { missing, ready: missing.length === 0 };
  }, [data, catalog]);

  const submitForm = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/${token}/submit`, { confirm: true });
      setState((s) => ({ ...s, status: "submitted" }));
      toast.success("Form submitted! HR will reach out shortly.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  if (state?.error) {
    return (
      <FullPage>
        <div className="text-center py-20">
          <X size={48} className="text-red-500 mx-auto" weight="bold" />
          <h1 className="text-2xl font-display mt-4">Link unavailable</h1>
          <p className="text-zinc-600 mt-2" data-testid="joiner-link-error">{state.error}</p>
          <p className="text-zinc-500 text-sm mt-4" data-testid="joiner-link-help">Please ask your HR contact to resend your onboarding link.</p>
        </div>
      </FullPage>
    );
  }

  if (!state || !catalog) return <FullPage><div className="text-center py-20 text-zinc-500">Loading…</div></FullPage>;

  if (state.status === "submitted" || state.status === "converted") {
    return (
      <FullPage>
        <div className="text-center py-20">
          <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle size={32} weight="fill" className="text-white" />
          </div>
          <h1 className="text-2xl font-display mt-6">All set, {state.candidate_name?.split(" ")[0]}!</h1>
          <p className="text-zinc-600 mt-2 max-w-md mx-auto">
            Your onboarding form is with the HR team at <strong>{state.company?.name || "your new company"}</strong>.
            We'll send you login details closer to your joining date{state.proposed_doj ? ` (${state.proposed_doj})` : ""}.
          </p>
        </div>
      </FullPage>
    );
  }

  const sec = SECTIONS[step];

  return (
    <FullPage>
      <Hero state={state} />

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm mb-8">
        <ProgressBar pct={state.completion_pct || 0} />
        <div className="grid grid-cols-12 min-h-[600px]">
          {/* Steps sidebar */}
          <div className="col-span-3 bg-zinc-50 border-r border-zinc-200 p-4 hidden md:block">
            {SECTIONS.map((s, i) => {
              const filled = !!data[s.key];
              return (
                <button
                  key={s.key}
                  onClick={() => setStep(i)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition mb-1 ${
                    step === i ? "bg-zinc-950 text-white" : "hover:bg-white text-zinc-700"
                  }`}
                  data-testid={`step-tab-${s.key}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-xs">{i + 1}. {s.title}</span>
                    {filled && step !== i && <CheckCircle size={14} className="text-emerald-500" weight="fill" />}
                    {s.required && !filled && step !== i && <span className="text-[10px] text-red-500">*</span>}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Section body */}
          <div className="col-span-12 md:col-span-9 p-6 lg:p-10">
            <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-1">
              Step {step + 1} of {SECTIONS.length}
            </div>
            <h2 className="text-2xl font-display mb-6">{sec.title}{sec.required && <span className="text-red-500">*</span>}</h2>

            <SectionForm
              section={sec}
              data={data[sec.key] || {}}
              setData={(d) => setData((all) => ({ ...all, [sec.key]: d }))}
              catalog={catalog}
              allData={data}
              state={state}
              uploads={state.uploads || {}}
              uploadFile={uploadFile}
              removeUpload={removeUpload}
              uploading={uploading}
            />

            <div className="flex items-center justify-between gap-3 mt-10 pt-6 border-t border-zinc-100">
              <Button
                variant="outline"
                disabled={step === 0}
                onClick={() => setStep((s) => s - 1)}
                data-testid="prev-btn"
              ><ArrowLeft size={16} className="mr-1.5" /> Previous</Button>

              <div className="flex items-center gap-3">
                {!sec.isReview && (
                  <Button
                    variant="outline"
                    disabled={saving}
                    onClick={() => saveSection(sec.key, data[sec.key] || {})}
                    data-testid="save-btn"
                  ><FloppyDisk size={16} className="mr-1.5" /> {saving ? "Saving…" : "Save"}</Button>
                )}

                {step < SECTIONS.length - 1 ? (
                  <Button
                    className="bg-zinc-950 hover:bg-zinc-800 text-white"
                    onClick={async () => {
                      if (!sec.isReview) await saveSection(sec.key, data[sec.key] || {});
                      setStep((s) => s + 1);
                    }}
                    data-testid="next-btn"
                  >Next <ArrowRight size={16} className="ml-1.5" /></Button>
                ) : (
                  <Button
                    disabled={submitting || !submitReadiness.ready}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60"
                    onClick={submitForm}
                    data-testid="submit-form-btn"
                    title={submitReadiness.ready ? "" : `Still missing: ${submitReadiness.missing.join(", ")}`}
                  >{submitting ? "Submitting…" : submitReadiness.ready ? "Submit my form" : `Complete required (${submitReadiness.missing.length} left)`}</Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <p className="text-center text-xs text-zinc-500 max-w-md mx-auto">
        Your data is saved automatically each time you click Save or Next. The link works on any device until {fmtDateIN(state.expires_at)}.
      </p>
    </FullPage>
  );
}

function FullPage({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-zinc-50 to-zinc-100">
      <div className="max-w-5xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
        {children}
      </div>
    </div>
  );
}

function Hero({ state }) {
  return (
    <div className="mb-8">
      <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-amber-700 font-semibold bg-amber-100/60 border border-amber-200 px-3 py-1 rounded-full">
        <Sparkle size={12} weight="duotone" /> {state.company?.name || "Welcome aboard"}
      </div>
      <h1 className="font-display text-3xl lg:text-4xl mt-3">Welcome, {state.candidate_name?.split(" ")[0]}.</h1>
      <p className="text-zinc-700 mt-2 max-w-2xl">
        A few details before day one — should take about 20 minutes. You can save and resume any time from any
        device. The information goes straight to your new HR team and helps get your laptop, ID, payroll, and
        benefits ready on time.
      </p>
    </div>
  );
}

function ProgressBar({ pct }) {
  return (
    <div className="px-6 py-3 border-b border-zinc-100 flex items-center gap-4">
      <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} data-testid="progress-bar" />
      </div>
      <div className="text-sm font-medium text-zinc-700" data-testid="progress-pct">{pct}%</div>
    </div>
  );
}

// ===========================================================================
// SECTION FORM ROUTER
// ===========================================================================
function SectionForm(props) {
  const { section } = props;
  const map = {
    personal: PersonalForm, contact: ContactForm, emergency_contacts: EmergencyForm,
    statutory_ids: StatutoryForm, education: EducationForm, previous_employment: EmploymentForm,
    banking: BankingForm, family: FamilyForm, skills: SkillsForm, references: ReferencesForm,
    medical: MedicalForm, visa: VisaForm, background_consent: ConsentForm,
    policy_acknowledgments: PolicyForm, joining_logistics: LogisticsForm, review: ReviewForm,
  };
  const Cmp = map[section.key] || (() => null);
  return <Cmp {...props} />;
}

// ---- Personal -----------------------------------------------------------
function PersonalForm({ data, setData, catalog, uploads, uploadFile, removeUpload, uploading }) {
  const f = (k, v) => setData({ ...data, [k]: v });
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Field label="Salutation"><DropOf options={catalog.catalog.salutations} v={data.salutation} on={(x) => f("salutation", x)} testid="salutation" /></Field>
      <Field label="Full legal name (matches Aadhaar)" required><Input value={data.full_legal_name || ""} onChange={(e) => f("full_legal_name", e.target.value)} data-testid="full-legal-name" /></Field>
      <Field label="Preferred / display name"><Input value={data.preferred_name || ""} onChange={(e) => f("preferred_name", e.target.value)} data-testid="preferred-name" /></Field>
      <Field label="Father's name" required><Input value={data.father_name || ""} onChange={(e) => f("father_name", e.target.value)} data-testid="father-name" /></Field>
      <Field label="Mother's name" required><Input value={data.mother_name || ""} onChange={(e) => f("mother_name", e.target.value)} data-testid="mother-name" /></Field>
      <Field label="Date of birth" required><DateInput value={data.date_of_birth || ""} onChange={(e) => f("date_of_birth", e.target.value)} data-testid="dob" /></Field>
      <Field label="Gender" required><DropOf options={catalog.catalog.genders} v={data.gender} on={(x) => f("gender", x)} testid="gender" /></Field>
      <Field label="Blood group"><DropOf options={catalog.catalog.blood_groups} v={data.blood_group} on={(x) => f("blood_group", x)} testid="blood-group" /></Field>
      <Field label="Marital status"><DropOf options={catalog.catalog.marital_statuses} v={data.marital_status} on={(x) => f("marital_status", x)} testid="marital-status" /></Field>
      <Field label="Nationality"><Input value={data.nationality || "Indian"} onChange={(e) => f("nationality", e.target.value)} data-testid="nationality" /></Field>
      <Field label="Category (statutory reporting)"><DropOf options={catalog.catalog.categories} v={data.category} on={(x) => f("category", x)} testid="category" /></Field>
      <Field label="Languages spoken"><ChipInput values={data.languages || []} onChange={(arr) => f("languages", arr)} placeholder="Type a language and press Enter" testid="languages" /></Field>
      <div className="md:col-span-2"><UploadRow label="Passport-size photo" field_path="personal.photo" uploads={uploads} uploadFile={uploadFile} removeUpload={removeUpload} uploading={uploading} accept="image/*" /></div>
    </div>
  );
}

// ---- Contact ------------------------------------------------------------
function ContactForm({ data, setData }) {
  const f = (k, v) => setData({ ...data, [k]: v });
  const addr = (which, k, v) => setData({ ...data, [which]: { ...(data[which] || {}), [k]: v } });
  return (
    <div>
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <Field label="Personal email" required><Input type="email" value={data.personal_email || ""} onChange={(e) => f("personal_email", e.target.value)} data-testid="personal-email" /></Field>
        <Field label="Mobile (primary)" required><Input value={data.mobile_primary || ""} onChange={(e) => f("mobile_primary", e.target.value)} data-testid="mobile" placeholder="+91 98765 43210" /></Field>
        <Field label="Alternate mobile"><Input value={data.alt_mobile || ""} onChange={(e) => f("alt_mobile", e.target.value)} /></Field>
        <Field label="WhatsApp number"><Input value={data.whatsapp || ""} onChange={(e) => f("whatsapp", e.target.value)} /></Field>
      </div>
      <h3 className="text-sm font-semibold mt-2 mb-3">Current address</h3>
      <AddressBlock which="current_address" data={data.current_address || {}} on={(k, v) => addr("current_address", k, v)} />
      <label className="flex items-center gap-2 mt-4 text-sm">
        <input type="checkbox" checked={!!data.same_as_current} onChange={(e) => {
          const same = e.target.checked;
          setData({ ...data, same_as_current: same, permanent_address: same ? data.current_address : (data.permanent_address || {}) });
        }} data-testid="same-as-current" />
        Permanent address is same as current
      </label>
      {!data.same_as_current && (
        <>
          <h3 className="text-sm font-semibold mt-6 mb-3">Permanent address</h3>
          <AddressBlock which="permanent_address" data={data.permanent_address || {}} on={(k, v) => addr("permanent_address", k, v)} />
        </>
      )}
    </div>
  );
}
function AddressBlock({ which, data, on }) {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Field label="Address line 1"><Input value={data.line1 || ""} onChange={(e) => on("line1", e.target.value)} data-testid={`${which}-line1`} /></Field>
      <Field label="Address line 2"><Input value={data.line2 || ""} onChange={(e) => on("line2", e.target.value)} /></Field>
      <Field label="City"><Input value={data.city || ""} onChange={(e) => on("city", e.target.value)} data-testid={`${which}-city`} /></Field>
      <Field label="State"><Input value={data.state || ""} onChange={(e) => on("state", e.target.value)} data-testid={`${which}-state`} /></Field>
      <Field label="Pincode"><Input value={data.pincode || ""} onChange={(e) => on("pincode", e.target.value)} data-testid={`${which}-pincode`} /></Field>
      <Field label="Country"><Input value={data.country || "India"} onChange={(e) => on("country", e.target.value)} /></Field>
    </div>
  );
}

// ---- Emergency contacts -------------------------------------------------
function EmergencyForm({ data, setData }) {
  const update = (i, k, v) => {
    const arr = [...(data.contacts || [{}, {}])];
    arr[i] = { ...(arr[i] || {}), [k]: v };
    setData({ ...data, contacts: arr });
  };
  const contacts = data.contacts || [{}, {}];
  return (
    <div className="space-y-6">
      {contacts.slice(0, 3).map((c, i) => (
        <div key={i} className="border border-zinc-200 rounded-lg p-4">
          <div className="text-sm font-semibold mb-3">Contact {i + 1}{i === 0 ? " *" : " (optional)"}</div>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Name" required={i === 0}><Input value={c.name || ""} onChange={(e) => update(i, "name", e.target.value)} data-testid={`emerg-name-${i}`} /></Field>
            <Field label="Relation"><Input value={c.relation || ""} onChange={(e) => update(i, "relation", e.target.value)} placeholder="Spouse / Parent / Sibling" /></Field>
            <Field label="Phone" required={i === 0}><Input value={c.phone || ""} onChange={(e) => update(i, "phone", e.target.value)} data-testid={`emerg-phone-${i}`} /></Field>
            <Field label="Email"><Input value={c.email || ""} onChange={(e) => update(i, "email", e.target.value)} /></Field>
            <div className="md:col-span-2">
              <Field label="Address"><Textarea rows={2} value={c.address || ""} onChange={(e) => update(i, "address", e.target.value)} /></Field>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---- Statutory IDs ------------------------------------------------------
function StatutoryForm({ data, setData, uploads, uploadFile, removeUpload, uploading }) {
  const f = (k, v) => setData({ ...data, [k]: v });
  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="PAN" required={!data.no_pan}><Input value={data.pan || ""} disabled={!!data.no_pan} onChange={(e) => f("pan", e.target.value.toUpperCase())} placeholder="ABCDE1234F" data-testid="pan" /></Field>
        <Field label="Aadhaar (12 digits)" required><Input value={data.aadhaar || ""} onChange={(e) => f("aadhaar", e.target.value)} data-testid="aadhaar" /></Field>
        <Field label="UAN (PF) — if any"><Input value={data.uan || ""} onChange={(e) => f("uan", e.target.value)} /></Field>
        <Field label="Old PF number — if transferring"><Input value={data.old_pf || ""} onChange={(e) => f("old_pf", e.target.value)} /></Field>
        <Field label="ESIC number"><Input value={data.esic || ""} onChange={(e) => f("esic", e.target.value)} /></Field>
        <Field label="Passport number"><Input value={data.passport_number || ""} onChange={(e) => f("passport_number", e.target.value)} /></Field>
        <Field label="Passport expiry"><DateInput value={data.passport_expiry || ""} onChange={(e) => f("passport_expiry", e.target.value)} /></Field>
        <Field label="Driving license"><Input value={data.driving_license || ""} onChange={(e) => f("driving_license", e.target.value)} /></Field>
        <Field label="Voter ID"><Input value={data.voter_id || ""} onChange={(e) => f("voter_id", e.target.value)} /></Field>
        <label className="flex items-center gap-2 text-sm md:col-span-2">
          <input type="checkbox" checked={!!data.no_pan} onChange={(e) => setData({ ...data, no_pan: e.target.checked, pan: e.target.checked ? "" : data.pan })} data-testid="no-pan" />
          I don't have a PAN yet (HR will collect it later)
        </label>
      </div>
      <UploadRow label="PAN card" field_path="statutory.pan_card" uploads={uploads} uploadFile={uploadFile} removeUpload={removeUpload} uploading={uploading} />
      <UploadRow label="Aadhaar card (front + back)" field_path="statutory.aadhaar_card" uploads={uploads} uploadFile={uploadFile} removeUpload={removeUpload} uploading={uploading} />
      <UploadRow label="Passport (if applicable)" field_path="statutory.passport" uploads={uploads} uploadFile={uploadFile} removeUpload={removeUpload} uploading={uploading} />
    </div>
  );
}

// ---- Education ----------------------------------------------------------
function EducationForm({ data, setData, catalog, uploads, uploadFile, removeUpload, uploading }) {
  const entries = data.entries || [];
  const add = () => setData({ entries: [...entries, {}] });
  const upd = (i, k, v) => {
    const arr = [...entries]; arr[i] = { ...arr[i], [k]: v }; setData({ entries: arr });
  };
  const remove = (i) => setData({ entries: entries.filter((_, idx) => idx !== i) });
  return (
    <div className="space-y-4">
      {entries.length === 0 && <div className="text-sm text-zinc-500">No education added yet.</div>}
      {entries.map((e, i) => (
        <div key={i} className="border border-zinc-200 rounded-lg p-4" data-testid={`edu-${i}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">Entry {i + 1}</div>
            <Button variant="ghost" size="sm" onClick={() => remove(i)}><Trash size={14} /></Button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Qualification level"><DropOf options={catalog.catalog.qualification_levels} v={e.level} on={(x) => upd(i, "level", x)} /></Field>
            <Field label="Course / Degree"><Input value={e.course || ""} onChange={(ev) => upd(i, "course", ev.target.value)} /></Field>
            <Field label="Specialization"><Input value={e.specialization || ""} onChange={(ev) => upd(i, "specialization", ev.target.value)} /></Field>
            <Field label="Institution"><Input value={e.institution || ""} onChange={(ev) => upd(i, "institution", ev.target.value)} /></Field>
            <Field label="Board / University"><Input value={e.board || ""} onChange={(ev) => upd(i, "board", ev.target.value)} /></Field>
            <Field label="Country"><Input value={e.country || "India"} onChange={(ev) => upd(i, "country", ev.target.value)} /></Field>
            <Field label="Mode"><DropOf options={catalog.catalog.study_modes} v={e.mode} on={(x) => upd(i, "mode", x)} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Start year"><Input type="number" value={e.start_year || ""} onChange={(ev) => upd(i, "start_year", ev.target.value)} /></Field>
              <Field label="End year"><Input type="number" value={e.end_year || ""} onChange={(ev) => upd(i, "end_year", ev.target.value)} /></Field>
            </div>
            <Field label="Grade / CGPA / %"><Input value={e.grade || ""} onChange={(ev) => upd(i, "grade", ev.target.value)} /></Field>
            <div className="md:col-span-2">
              <UploadRow label="Marksheet / Certificate" field_path={`education.${i}.marksheet`} uploads={uploads} uploadFile={uploadFile} removeUpload={removeUpload} uploading={uploading} />
            </div>
          </div>
        </div>
      ))}
      <Button variant="outline" onClick={add} data-testid="edu-add"><Plus size={14} className="mr-1" /> Add education</Button>
    </div>
  );
}

// ---- Previous employment ------------------------------------------------
function EmploymentForm({ data, setData, catalog, uploads, uploadFile, removeUpload, uploading }) {
  const entries = data.entries || [];
  const add = () => setData({ entries: [...entries, {}] });
  const upd = (i, k, v) => { const a = [...entries]; a[i] = { ...a[i], [k]: v }; setData({ entries: a }); };
  const remove = (i) => setData({ entries: entries.filter((_, x) => x !== i) });
  return (
    <div className="space-y-4">
      {entries.length === 0 && <div className="text-sm text-zinc-500">First job? Skip this — no entries needed.</div>}
      {entries.map((e, i) => (
        <div key={i} className="border border-zinc-200 rounded-lg p-4" data-testid={`emp-${i}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">Job {i + 1}</div>
            <Button variant="ghost" size="sm" onClick={() => remove(i)}><Trash size={14} /></Button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Company name"><Input value={e.company || ""} onChange={(ev) => upd(i, "company", ev.target.value)} /></Field>
            <Field label="Job title"><Input value={e.title || ""} onChange={(ev) => upd(i, "title", ev.target.value)} /></Field>
            <Field label="Employment type"><DropOf options={catalog.catalog.employment_types} v={e.type} on={(x) => upd(i, "type", x)} /></Field>
            <Field label="Annual CTC at exit (₹)"><Input type="number" value={e.ctc || ""} onChange={(ev) => upd(i, "ctc", ev.target.value)} /></Field>
            <Field label="From"><DateInput value={e.from_date || ""} onChange={(ev) => upd(i, "from_date", ev.target.value)} /></Field>
            <Field label="To"><DateInput value={e.to_date || ""} onChange={(ev) => upd(i, "to_date", ev.target.value)} disabled={e.is_current} /></Field>
            <label className="flex items-center gap-2 text-sm col-span-2">
              <input type="checkbox" checked={!!e.is_current} onChange={(ev) => upd(i, "is_current", ev.target.checked)} />
              I currently work here
            </label>
            <Field label="Reason for leaving"><DropOf options={catalog.catalog.exit_reasons} v={e.exit_reason} on={(x) => upd(i, "exit_reason", x)} /></Field>
            <Field label="Manager name"><Input value={e.manager_name || ""} onChange={(ev) => upd(i, "manager_name", ev.target.value)} /></Field>
            <Field label="Manager email"><Input value={e.manager_email || ""} onChange={(ev) => upd(i, "manager_email", ev.target.value)} /></Field>
            <Field label="Manager phone"><Input value={e.manager_phone || ""} onChange={(ev) => upd(i, "manager_phone", ev.target.value)} /></Field>
          </div>
          <div className="mt-3 space-y-2">
            <UploadRow label="Offer letter" field_path={`previous_employment.${i}.offer_letter`} uploads={uploads} uploadFile={uploadFile} removeUpload={removeUpload} uploading={uploading} />
            <UploadRow label="Relieving letter" field_path={`previous_employment.${i}.relieving_letter`} uploads={uploads} uploadFile={uploadFile} removeUpload={removeUpload} uploading={uploading} />
            <UploadRow label="Last 3 payslips" field_path={`previous_employment.${i}.payslips`} uploads={uploads} uploadFile={uploadFile} removeUpload={removeUpload} uploading={uploading} />
            <UploadRow label="Form 16" field_path={`previous_employment.${i}.form16`} uploads={uploads} uploadFile={uploadFile} removeUpload={removeUpload} uploading={uploading} />
          </div>
        </div>
      ))}
      <Button variant="outline" onClick={add} data-testid="emp-add"><Plus size={14} className="mr-1" /> Add employment</Button>
    </div>
  );
}

// ---- Banking ------------------------------------------------------------
function BankingForm({ data, setData, catalog, uploads, uploadFile, removeUpload, uploading }) {
  const f = (k, v) => setData({ ...data, [k]: v });
  const [lookup, setLookup] = useState({ busy: false });

  const findIFSC = async () => {
    if (!data.ifsc) return;
    setLookup({ busy: true });
    try {
      const r = await axios.get(`${API}/ifsc/${data.ifsc.toUpperCase()}`);
      if (r.data.ok) {
        setData({ ...data, ifsc: r.data.ifsc, bank_name: r.data.bank, branch_name: r.data.branch, branch_city: r.data.city });
        toast.success(`${r.data.bank} — ${r.data.branch}`);
      } else { toast.error("IFSC not found"); }
    } catch { toast.error("Lookup failed"); }
    finally { setLookup({ busy: false }); }
  };

  const cash = !!data.cash_payment;
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <label className="flex items-center gap-2 text-sm md:col-span-2">
        <input type="checkbox" checked={cash} onChange={(e) => f("cash_payment", e.target.checked)} data-testid="cash-payment" />
        I don't have a bank account — I'll be paid in cash
      </label>
      {!cash && <>
      <Field label="Account holder name (as per bank)" required><Input value={data.account_holder || ""} onChange={(e) => f("account_holder", e.target.value)} data-testid="acct-holder" /></Field>
      <Field label="Account number" required><Input value={data.account_number || ""} onChange={(e) => f("account_number", e.target.value)} data-testid="acct-num" /></Field>
      <Field label="Re-enter account number" required><Input value={data.account_number_confirm || ""} onChange={(e) => f("account_number_confirm", e.target.value)} data-testid="acct-num-confirm" /></Field>
      {data.account_number_confirm && data.account_number && data.account_number !== data.account_number_confirm && (
        <div className="md:col-span-2 text-xs text-red-600" data-testid="acct-num-mismatch">Account numbers don't match — please re-check.</div>
      )}
      <Field label="Account type"><DropOf options={catalog.catalog.account_types} v={data.account_type} on={(x) => f("account_type", x)} /></Field>
      <div className="md:col-span-2">
        <Field label="IFSC code" required>
          <div className="flex gap-2">
            <Input value={data.ifsc || ""} onChange={(e) => f("ifsc", e.target.value.toUpperCase())} className="font-mono" data-testid="ifsc" />
            <Button disabled={!data.ifsc || lookup.busy} onClick={findIFSC} variant="outline" data-testid="ifsc-lookup">{lookup.busy ? "Looking up…" : "Lookup"}</Button>
          </div>
        </Field>
      </div>
      <Field label="Bank name"><Input value={data.bank_name || ""} readOnly className="bg-zinc-50" data-testid="bank-name" /></Field>
      <Field label="Branch"><Input value={data.branch_name || ""} readOnly className="bg-zinc-50" /></Field>
      <div className="md:col-span-2">
        <UploadRow label="Cancelled cheque / Passbook page (required)" field_path="banking.cheque" uploads={uploads} uploadFile={uploadFile} removeUpload={removeUpload} uploading={uploading} />
      </div>
      </>}
    </div>
  );
}

// ---- Family / Dependents ------------------------------------------------
function FamilyForm({ data, setData, catalog }) {
  const entries = data.dependents || [];
  const add = () => setData({ dependents: [...entries, {}] });
  const upd = (i, k, v) => { const a = [...entries]; a[i] = { ...a[i], [k]: v }; setData({ dependents: a }); };
  const remove = (i) => setData({ dependents: entries.filter((_, x) => x !== i) });
  return (
    <div className="space-y-4">
      <p className="text-sm text-zinc-600">Add family members for medical insurance, PF, and gratuity nominees. Nominee % must total 100%.</p>
      {entries.map((e, i) => (
        <div key={i} className="border border-zinc-200 rounded-lg p-4" data-testid={`fam-${i}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">Member {i + 1}</div>
            <Button variant="ghost" size="sm" onClick={() => remove(i)}><Trash size={14} /></Button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Name"><Input value={e.name || ""} onChange={(ev) => upd(i, "name", ev.target.value)} /></Field>
            <Field label="Relation"><DropOf options={catalog.catalog.dependent_relations} v={e.relation} on={(x) => upd(i, "relation", x)} /></Field>
            <Field label="Date of birth"><DateInput value={e.dob || ""} onChange={(ev) => upd(i, "dob", ev.target.value)} /></Field>
            <Field label="Aadhaar"><Input value={e.aadhaar || ""} onChange={(ev) => upd(i, "aadhaar", ev.target.value)} /></Field>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!e.dependent_for_medical} onChange={(ev) => upd(i, "dependent_for_medical", ev.target.checked)} /> Cover in medical insurance</label>
            <Field label="PF/Gratuity nominee %"><Input type="number" value={e.nominee_pct || ""} onChange={(ev) => upd(i, "nominee_pct", ev.target.value)} /></Field>
          </div>
        </div>
      ))}
      <Button variant="outline" onClick={add} data-testid="fam-add"><Plus size={14} className="mr-1" /> Add family member</Button>
    </div>
  );
}

// ---- Skills -------------------------------------------------------------
function SkillsForm({ data, setData }) {
  const f = (k, v) => setData({ ...data, [k]: v });
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Field label="Skills (comma-separated)"><Textarea rows={2} value={(data.skills || []).join(", ")} onChange={(e) => f("skills", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} data-testid="skills" placeholder="React, Python, AWS, Figma…" /></Field>
      <Field label="Certifications (comma-separated)"><Textarea rows={2} value={(data.certs || []).join(", ")} onChange={(e) => f("certs", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} placeholder="AWS-SAA, PMP, CFA…" /></Field>
      <Field label="LinkedIn URL"><Input value={data.linkedin || ""} onChange={(e) => f("linkedin", e.target.value)} placeholder="linkedin.com/in/your-handle" /></Field>
      <Field label="GitHub / Portfolio"><Input value={data.portfolio || ""} onChange={(e) => f("portfolio", e.target.value)} /></Field>
      <Field label="Hobbies & interests"><Input value={data.hobbies || ""} onChange={(e) => f("hobbies", e.target.value)} /></Field>
      <Field label="Awards / Publications"><Textarea rows={2} value={data.awards || ""} onChange={(e) => f("awards", e.target.value)} /></Field>
    </div>
  );
}

// ---- References ---------------------------------------------------------
function ReferencesForm({ data, setData, catalog }) {
  const entries = data.references || [];
  const add = () => setData({ references: [...entries, {}] });
  const upd = (i, k, v) => { const a = [...entries]; a[i] = { ...a[i], [k]: v }; setData({ references: a }); };
  const remove = (i) => setData({ references: entries.filter((_, x) => x !== i) });
  return (
    <div className="space-y-4">
      <p className="text-sm text-zinc-600">Add 2-3 professional references (not family). We may contact them with your permission.</p>
      {entries.slice(0, 4).map((r, i) => (
        <div key={i} className="border border-zinc-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">Reference {i + 1}</div>
            <Button variant="ghost" size="sm" onClick={() => remove(i)}><Trash size={14} /></Button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Name"><Input value={r.name || ""} onChange={(e) => upd(i, "name", e.target.value)} /></Field>
            <Field label="Relation"><DropOf options={catalog.catalog.reference_relations} v={r.relation} on={(x) => upd(i, "relation", x)} /></Field>
            <Field label="Company"><Input value={r.company || ""} onChange={(e) => upd(i, "company", e.target.value)} /></Field>
            <Field label="Designation"><Input value={r.designation || ""} onChange={(e) => upd(i, "designation", e.target.value)} /></Field>
            <Field label="Email"><Input value={r.email || ""} onChange={(e) => upd(i, "email", e.target.value)} /></Field>
            <Field label="Phone"><Input value={r.phone || ""} onChange={(e) => upd(i, "phone", e.target.value)} /></Field>
          </div>
        </div>
      ))}
      <Button variant="outline" onClick={add}><Plus size={14} className="mr-1" /> Add reference</Button>
    </div>
  );
}

// ---- Medical ------------------------------------------------------------
function MedicalForm({ data, setData, uploads, uploadFile, removeUpload, uploading }) {
  const f = (k, v) => setData({ ...data, [k]: v });
  return (
    <div className="space-y-4">
      <p className="text-sm text-zinc-600">This section is optional but helps in emergencies. Information is private and access-restricted.</p>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Allergies"><Input value={data.allergies || ""} onChange={(e) => f("allergies", e.target.value)} /></Field>
        <Field label="Chronic conditions"><Input value={data.conditions || ""} onChange={(e) => f("conditions", e.target.value)} /></Field>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!data.disability} onChange={(e) => f("disability", e.target.checked)} /> I'm registered with a disability</label>
        <Field label="Disability details"><Input value={data.disability_detail || ""} onChange={(e) => f("disability_detail", e.target.value)} disabled={!data.disability} /></Field>
        <Field label="Family doctor name"><Input value={data.doctor_name || ""} onChange={(e) => f("doctor_name", e.target.value)} /></Field>
        <Field label="Doctor phone"><Input value={data.doctor_phone || ""} onChange={(e) => f("doctor_phone", e.target.value)} /></Field>
      </div>
      <UploadRow label="Pre-employment medical certificate" field_path="medical.cert" uploads={uploads} uploadFile={uploadFile} removeUpload={removeUpload} uploading={uploading} />
      <UploadRow label="COVID vaccination certificate" field_path="medical.covid" uploads={uploads} uploadFile={uploadFile} removeUpload={removeUpload} uploading={uploading} />
    </div>
  );
}

// ---- Visa ---------------------------------------------------------------
function VisaForm({ data, setData }) {
  const f = (k, v) => setData({ ...data, [k]: v });
  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!data.visa_needed} onChange={(e) => f("visa_needed", e.target.checked)} /> I need a work visa / permit</label>
      {data.visa_needed && (
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Visa type"><Input value={data.visa_type || ""} onChange={(e) => f("visa_type", e.target.value)} /></Field>
          <Field label="Visa expiry"><DateInput value={data.visa_expiry || ""} onChange={(e) => f("visa_expiry", e.target.value)} /></Field>
          <Field label="Country of work permit"><Input value={data.visa_country || ""} onChange={(e) => f("visa_country", e.target.value)} /></Field>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!data.sponsor_required} onChange={(e) => f("sponsor_required", e.target.checked)} /> Need company sponsorship</label>
        </div>
      )}
    </div>
  );
}

// ---- Background consent -------------------------------------------------
function ConsentForm({ data, setData }) {
  const f = (k, v) => setData({ ...data, [k]: v });
  return (
    <div className="space-y-3">
      <p className="text-sm text-zinc-700">By ticking each box below, you authorize the company to:</p>
      {[
        ["bgv_consent", "Conduct background verification (criminal record, academic credentials, employment history)"],
        ["posh_briefing_consent", "Brief me on the PoSH (Prevention of Sexual Harassment) policy and committee"],
        ["family_contact_consent", "Contact my emergency contacts in case of medical emergency"],
        ["reference_check_consent", "Reach out to references I've listed"],
      ].map(([k, label]) => (
        <label key={k} className={`flex items-start gap-3 border rounded-lg p-3 cursor-pointer ${data[k] ? "bg-emerald-50 border-emerald-200" : "border-zinc-200"}`}>
          <input type="checkbox" checked={!!data[k]} onChange={(e) => f(k, e.target.checked)} className="mt-1" data-testid={k} />
          <span className="text-sm">{label}</span>
        </label>
      ))}
      <p className="text-xs text-zinc-500 mt-3">Your consent is timestamped and audit-logged.</p>
    </div>
  );
}

// ---- Policy acknowledgments --------------------------------------------
function PolicyForm({ data, setData, catalog }) {
  const f = (k, v) => setData({ ...data, [k]: v });
  const [reading, setReading] = useState(null); // {key, title, body} of the open modal

  const accept = (key) => {
    f(key, true);
    setReading(null);
    toast.success("Acknowledged");
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-zinc-700">Click <strong>Read policy</strong> on each item below to review the full text. Tick the box once you've read and agreed. Each acknowledgement is audit-logged with a timestamp.</p>
      {(catalog.policies || []).map((p) => {
        const accepted = !!data[p.key];
        return (
          <div
            key={p.key}
            className={`border rounded-lg p-3 ${accepted ? "bg-emerald-50 border-emerald-200" : "border-zinc-200 bg-white"}`}
            data-testid={`policy-${p.key}`}
          >
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => f(p.key, e.target.checked)}
                className="mt-1"
                aria-label={`I acknowledge ${p.title}`}
                data-testid={`policy-check-${p.key}`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-sm font-medium">{p.title}</div>
                  {p.body && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 gap-1.5 text-xs"
                      onClick={() => setReading(p)}
                      data-testid={`policy-read-${p.key}`}
                    >
                      <BookOpen size={13}/> Read policy
                    </Button>
                  )}
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  {accepted
                    ? <span className="text-emerald-700">✓ I have read and agreed to this policy.</span>
                    : <>Click <em>Read policy</em> to view the full text, then tick the box to confirm.</>
                  }
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <Dialog open={!!reading} onOpenChange={(o) => { if (!o) setReading(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col" data-testid="policy-modal">
          <DialogHeader>
            <DialogTitle>{reading?.title}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-1 py-2 text-sm text-zinc-700 leading-relaxed" data-testid="policy-modal-body">
            {renderPolicyBody(reading?.body || "Full policy text will be shared with your offer letter.")}
          </div>
          <DialogFooter className="border-t pt-3 mt-2">
            <Button type="button" variant="ghost" onClick={() => setReading(null)} data-testid="policy-modal-cancel">Close</Button>
            <Button
              type="button"
              onClick={() => reading && accept(reading.key)}
              disabled={!!(reading && data[reading.key])}
              data-testid="policy-modal-accept"
            >
              {reading && data[reading.key] ? "Already acknowledged" : "I've read this — accept"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---- Markdown-lite policy body renderer --------------------------------
// We only need bold + paragraphs + bullets. Keeping it dependency-free
// avoids pulling in a full markdown lib for 8 short policies.
function renderPolicyBody(text) {
  const renderInline = (line, idx) => {
    const parts = [];
    let remaining = line;
    let k = 0;
    while (remaining.length) {
      const m = remaining.match(/\*\*([^*]+)\*\*/);
      if (!m) { parts.push(<span key={`t-${idx}-${k++}`}>{remaining}</span>); break; }
      const before = remaining.slice(0, m.index);
      if (before) parts.push(<span key={`t-${idx}-${k++}`}>{before}</span>);
      parts.push(<strong key={`b-${idx}-${k++}`}>{m[1]}</strong>);
      remaining = remaining.slice(m.index + m[0].length);
    }
    return parts;
  };
  return (text || "").split("\n\n").map((para, i) => {
    if (para.trim().startsWith("•")) {
      const items = para.split("\n").map(s => s.replace(/^•\s*/, "").trim()).filter(Boolean);
      return <ul key={i} className="list-disc pl-5 my-2 space-y-1">{items.map((it, j) => <li key={j}>{renderInline(it, `${i}-${j}`)}</li>)}</ul>;
    }
    return <p key={i} className="my-2">{renderInline(para, i)}</p>;
  });
}

// ---- Chip / tag input --------------------------------------------------
function ChipInput({ values = [], onChange, placeholder, testid }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const t = draft.trim().replace(/,$/, "");
    if (!t) return;
    if (values.includes(t)) { setDraft(""); return; }
    onChange([...values, t]);
    setDraft("");
  };
  const remove = (i) => onChange(values.filter((_, idx) => idx !== i));
  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); }
    else if (e.key === "Backspace" && !draft && values.length) { remove(values.length - 1); }
  };
  return (
    <div className="border rounded-md bg-white p-1.5 flex flex-wrap gap-1.5 min-h-[42px] focus-within:ring-1 focus-within:ring-zinc-400">
      {values.map((v, i) => (
        <span key={`${v}-${i}`} className="inline-flex items-center gap-1 bg-zinc-100 text-zinc-800 rounded px-2 py-0.5 text-sm" data-testid={`${testid}-chip-${i}`}>
          {v}
          <button type="button" onClick={() => remove(i)} className="text-zinc-500 hover:text-zinc-900" aria-label={`Remove ${v}`} data-testid={`${testid}-chip-remove-${i}`}>
            <X size={11}/>
          </button>
        </span>
      ))}
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKey}
        onBlur={add}
        placeholder={values.length ? "" : (placeholder || "Type and press Enter")}
        className="flex-1 min-w-[120px] border-0 outline-none text-sm bg-transparent px-1"
        data-testid={testid}
      />
    </div>
  );
}

// ---- Joining logistics --------------------------------------------------
function LogisticsForm({ data, setData, catalog, state }) {
  const f = (k, v) => setData({ ...data, [k]: v });
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Field label="Confirmed date of joining"><DateInput value={data.confirmed_doj || state.proposed_doj || ""} onChange={(e) => f("confirmed_doj", e.target.value)} data-testid="confirmed-doj" /></Field>
      <Field label="T-shirt size"><DropOf options={catalog.catalog.t_shirt_sizes} v={data.tshirt} on={(x) => f("tshirt", x)} data-testid="tshirt" /></Field>
      <Field label="Dietary preference"><DropOf options={catalog.catalog.dietary_prefs} v={data.dietary} on={(x) => f("dietary", x)} /></Field>
      <Field label="Laptop preference"><DropOf options={catalog.catalog.laptop_prefs} v={data.laptop} on={(x) => f("laptop", x)} /></Field>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!data.needs_accommodation} onChange={(e) => f("needs_accommodation", e.target.checked)} /> Need accommodation help</label>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!data.needs_relocation} onChange={(e) => f("needs_relocation", e.target.checked)} /> Need relocation assistance</label>
      <div className="md:col-span-2"><Field label="Anything else we should know?"><Textarea rows={3} value={data.notes || ""} onChange={(e) => f("notes", e.target.value)} /></Field></div>
    </div>
  );
}

// ---- Review -------------------------------------------------------------
function ReviewForm({ allData, uploads, catalog }) {
  const counts = useMemo(() => {
    return SECTIONS.filter((s) => !s.isReview).map((s) => {
      const filled = !!allData[s.key];
      return { ...s, filled };
    });
  }, [allData]);
  const filesCount = Object.keys(uploads || {}).length;
  return (
    <div>
      <p className="text-sm text-zinc-700 mb-4">Everything saved before submitting. Sections marked red are required and not yet filled.</p>
      <div className="grid md:grid-cols-2 gap-2">
        {counts.map((s) => (
          <div key={s.key} className={`flex items-center justify-between px-3 py-2 border rounded-md ${
            s.filled ? "bg-emerald-50 border-emerald-200" : s.required ? "bg-red-50 border-red-200" : "bg-zinc-50 border-zinc-200"
          }`} data-testid={`review-${s.key}`}>
            <div className="text-sm">{s.title}</div>
            {s.filled
              ? <CheckCircle size={16} className="text-emerald-600" weight="fill" />
              : s.required
                ? <span className="text-xs uppercase tracking-wider text-red-600 font-semibold">Required</span>
                : <span className="text-xs text-zinc-400">Optional</span>
            }
          </div>
        ))}
      </div>
      <div className="mt-6 text-sm text-zinc-600">{filesCount} file{filesCount === 1 ? "" : "s"} uploaded.</div>
    </div>
  );
}

// ===========================================================================
// COMMON FIELD HELPERS
// ===========================================================================
function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs">{label}{required && <span className="text-red-500">*</span>}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function DropOf({ options, v, on, testid }) {
  return (
    <Select value={v || ""} onValueChange={on}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="Select…" /></SelectTrigger>
      <SelectContent>{(options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function UploadRow({ label, field_path, uploads, uploadFile, removeUpload, uploading, accept }) {
  const file = uploads[field_path];
  const busy = uploading[field_path];
  return (
    <div className={`flex items-center justify-between gap-3 border rounded-md p-3 ${file ? "bg-white border-zinc-200" : "bg-zinc-50 border-zinc-200 border-dashed"}`} data-testid={`upload-row-${field_path}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{label}</div>
        {file && (
          <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1.5">
            <FileText size={11} /> {file.file_name}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1">
        {file && (
          <Button variant="ghost" size="icon" onClick={() => removeUpload(field_path)} data-testid={`remove-${field_path}`}>
            <Trash size={14} className="text-zinc-500" />
          </Button>
        )}
        <label className={`inline-flex items-center gap-1.5 text-xs font-medium border border-zinc-300 px-3 py-1.5 rounded-md cursor-pointer hover:bg-zinc-50 ${busy ? "opacity-50 pointer-events-none" : ""}`}>
          <ArrowUp size={12} weight="bold" /> {busy ? "Uploading…" : file ? "Replace" : "Upload"}
          <input type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadFile(field_path, f); }} accept={accept || ".pdf,.jpg,.jpeg,.png"} disabled={busy} />
        </label>
      </div>
    </div>
  );
}
